/* eslint-disable @typescript-eslint/ban-ts-comment */
import { setCookieByName, getCookieByName } from '.';
import get from 'lodash.get';
import { DiscoveryProduct, AddToCartType, CartOrderLine } from '../types';

export const saveUtmCookie = (query: any) => {
  const {
    utm_source = '',
    utm_campaign = '',
    utm_term = '',
    utm_content = '',
    utm_medium = '',
    gclid = '',
    fbclid = '',
  } = query;

  const currentDate = new Date();
  const date = new Date(
    currentDate.getFullYear() + 2,
    currentDate.getMonth(),
    currentDate.getDay(),
  );
  const expireTime = date.toUTCString();

  if (utm_source) {
    setCookieByName('utmSource', utm_source, expireTime);
    setCookieByName('utmCampaign', utm_campaign, expireTime);
    setCookieByName('utmTerm', utm_term, expireTime);
    setCookieByName('utmContent', utm_content, expireTime);
    setCookieByName('utmMedium', utm_medium, expireTime);
  }

  if (gclid) setCookieByName('gclid', gclid, expireTime);
  if (fbclid) setCookieByName('fbclid', fbclid, expireTime);
};

export const getUtmParams = () => {
  const utmSource = getCookieByName('utmSource') || '';
  const utmCampaign = getCookieByName('utmCampaign') || '';
  const utmTerm = getCookieByName('utmTerm') || '';
  const utmContent = getCookieByName('utmContent') || '';
  const utmMedium = getCookieByName('utmMedium') || '';
  return {
    utmSource,
    utmMedium,
    utmTerm,
    utmContent,
    utmCampaign,
  };
};

export const trackCheckout = (
  cart: any,
  paymentMethod?: string,
  payload?: any,
  response?: any,
  step?: number,
  type = 'order',
): void => {
  let orderId = get(response, 'data.createOrder.id');
  if (type === 'marketOrder') {
    orderId = get(response, 'data.createMarketOrder.id');
  }
  if (paymentMethod) {
    window.track('setOrder', {
      orderId,
      amountAfterDiscount: cart.order.grandTotal,
      amountBeforeDiscount: cart.order.orderLines.reduce(
        (acc: any, cur: any) => acc + cur.unitPrice * cur.quantity,
        0,
      ),
      paymentMethod,
      status: !!orderId ? 'success' : 'failed',
    });
  }

  if (payload && response) {
    cart.order.orderLines.forEach((item: any) =>
      window.track('addProduct', {
        skuId: item.sku,
        skuName: item.displayName,
        price: item.unitPrice,
        promotionPrice: item.price,
        quantity: item.quantity,
      }),
    );
    window.track('trackCheckoutOrder', {
      ...getUtmParams(),
      //payload
      attr4: JSON.stringify(payload),
      //response
      attr5: JSON.stringify(response),
    });
  }

  //GTM
  if (step) {
    // @ts-ignore
    window.dataLayer.push({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            step: step,
          },
          products: cart.order.orderLines.map((item: any) => ({
            name: item.name,
            id: item.sku,
            price: item.price,
            quantity: item.quantity,
            category: get(item, 'product.productInfo.categories.0.name', ''),
          })),
        },
      },
    });
  }
};

export const trackCheckoutV2 = (
  cart: any,
  paymentMethod?: string,
  payload?: any,
  response?: any,
  step?: number,
): void => {
  const orderId = response?.code;
  if (paymentMethod) {
    window.track('setOrder', {
      orderId,
      amountAfterDiscount: cart.order.grandTotal,
      amountBeforeDiscount: cart.order.orderLines.reduce(
        (acc: any, cur: any) => acc + cur.unitPrice * cur.quantity,
        0,
      ),
      paymentMethod,
      status: !!orderId ? 'success' : 'failed',
    });
  }

  if (payload && response) {
    cart.order.orderLines.forEach((item: any) =>
      window.track('addProduct', {
        skuId: item.sku,
        skuName: item.displayName,
        price: item.unitPrice,
        promotionPrice: item.price,
        quantity: item.quantity,
      }),
    );
    window.track('trackCheckoutOrder', {
      ...getUtmParams(),
      //payload
      attr4: JSON.stringify(payload),
      //response
      attr5: JSON.stringify(response),
    });
  }

  //GTM
  if (step) {
    // @ts-ignore
    window.dataLayer.push({
      event: 'checkout',
      ecommerce: {
        checkout: {
          actionField: {
            step: step,
          },
          products: cart.order.orderLines.map((item: any) => ({
            name: item.name,
            id: item.sku,
            price: item.price,
            quantity: item.quantity,
            category: get(item, 'product.productInfo.categories.0.name', ''),
          })),
        },
      },
    });
  }
};

export const trackViewDetail = (product: any): void => {
  // GTM
  window.dataLayer.push({
    event: 'viewDetail',
    ecommerce: {
      detail: {
        products: [
          {
            name: product.name, // Name or ID is required.
            id: product.sku,
            price: product.price,
            brand: product.brand,
            category: product.category,
          },
        ],
      },
    },
  });
};

interface TrackPaymentOrderItem {
  name: string;
  sku: string;
  quantityDecimal: number;
  price: number;
  rowTotal: number;
  unitPrice: number;
}

interface TrackPaymentOrder {
  id: string;
  paymentMethod: string;
  grandTotal: string;
  items: TrackPaymentOrderItem[];
}

export const trackPayment = (orderByCodeData: TrackPaymentOrder): void => {
  window.track('payment', {
    orderId: orderByCodeData.id,
    referral: window.location.href,
    paymentMethod: orderByCodeData.paymentMethod,
    status: 'success',
  });
  window.dataLayer.push({
    event: 'transaction',
    ecommerce: {
      purchase: {
        actionField: {
          id: orderByCodeData.id,
          revenue: orderByCodeData.grandTotal.toString(),
        },
        products: (orderByCodeData.items || []).map((product: any) => {
          return {
            quantity: product.quantityDecimal,
            price: (
              product.price ||
              product.rowTotal / product.quantityDecimal ||
              product.unitPrice ||
              ''
            ).toString(),
            name: product.name,
            id: product.sku,
            category: product.category,
          };
        }),
      },
    },
  });
};

export const trackAddToCart = (
  product: DiscoveryProduct & { addToCartPrice: number },
  selectedPromotionId?: string | number,
  type?: string,
  status?: string,
  cartId?: string,
) => {
  const { defaultPromotions = [] } = product;

  window.track(type ? type : AddToCartType.ADDTOCART, {
    cartId,
    skuId: product.productInfo.sku,
    skuName: product.productInfo.name,
    promotionId: `${[
      ...defaultPromotions.map((promo) => promo.id),
      selectedPromotionId || '',
    ].join(',')}`,
    status: status || 'success',
    quantity: 1,
    price: parseInt(product.prices[0].supplierRetailPrice),
    promotionPrice: product.addToCartPrice,
  });

  // GTM
  // @ts-ignore
  window.dataLayer.push({
    event: AddToCartType.ADDTOCART,
    ecommerce: {
      currencyCode: 'VND',
      add: {
        products: [
          {
            name: product.productInfo.name,
            id: product.productInfo.sku,
            price: product.addToCartPrice,
            quantity: 1,
            category: get(product, 'productInfo.categories.0.name', ''),
          },
        ],
      },
    },
  });
};

export const trackIncreaseQuantityProduct = (
  orderLine: any,
  diff = 1,
  cartId: string,
): void => {
  window.track('increaseQuantityProduct', {
    cartId,
    skuId: orderLine.sku,
    skuName: orderLine.name,
    status: 'success',
    quantity: orderLine.quantity + diff,
    price: orderLine.unitPrice,
    promotionPrice: orderLine.price,
  });

  //GTM
  // @ts-ignore
  window.dataLayer.push({
    event: AddToCartType.ADDTOCART,
    ecommerce: {
      currencyCode: 'VND',
      add: {
        products: [
          {
            name: orderLine.name,
            id: orderLine.sku,
            price: orderLine.price,
            quantity: diff,
            category: get(
              orderLine,
              'product.productInfo.categories.0.name',
              '',
            ),
          },
        ],
      },
    },
  });
};

export const trackDecreaseQuantityProduct = (
  orderLine: any,
  diff = 1,
  cartId: string,
): void => {
  window.track('decreaseQuantityProduct', {
    cartId,
    skuId: orderLine.sku,
    skuName: orderLine.name,
    status: 'success',
    quantity: orderLine.quantity - diff,
    price: orderLine.unitPrice,
    promotionPrice: orderLine.price,
  });
};

export const trackChangeQuantityProduct = (
  orderLine: any,
  newQty: number,
  cartId: string,
) => {
  const currQty = orderLine.quantity;
  if (currQty < newQty) {
    trackIncreaseQuantityProduct(orderLine, newQty - currQty, cartId);
  } else if (currQty > newQty) {
    trackDecreaseQuantityProduct(orderLine, currQty - newQty, cartId);
  }
};

export const trackRemoveFromCart = (
  orderLine: CartOrderLine,
  cartId: string,
): void => {
  window.track('removeFromCart', {
    cartId,
    skuId: orderLine.sku,
    skuName: orderLine.name,
    status: 'success',
    quantity: orderLine.quantity,
    price: orderLine.unitPrice,
    promotionPrice: orderLine.price,
  });

  window.dataLayer.push({
    event: 'removeFromCart',
    ecommerce: {
      remove: {
        products: [
          {
            name: orderLine.name,
            id: orderLine.sku,
            price: orderLine.price,
            quantity: orderLine.quantity,
            category: get(
              orderLine,
              'product.productInfo.categories.0.name',
              '',
            ),
          },
        ],
      },
    },
  });
};

export const trackSelectProduct = (
  orderLine: any,
  checked: boolean,
  cartId: string,
) => {
  window.track(checked ? 'selectProduct' : 'unselectProduct', {
    cartId,
    skuId: orderLine.sku,
    skuName: orderLine.name,
    price: orderLine.unitPrice,
    promotionPrice: orderLine.price,
    quantity: orderLine.quantity,
    status: 'success',
  });
};

export const trackSearchEvent = (keyword: string): void => {
  window.track('siteSearch', {
    params: '{}',
    keywords: keyword,
  });
  // @ts-ignore
  window.dataLayer.push({
    event: 'view_search_results',
    content_keyword: keyword,
  });
};

export const trackManually = (
  contentName: string | boolean,
  regionName: string,
  target: string,
  payload?: string | null,
): void => {
  window.track('manualTrackInteractionContent', {
    interaction: 'click',
    contentName,
    regionName,
    target,
    payload,
  });
};
